import { useState } from "react";
import type { ReactNode } from "react";
import { Alert, Button, EmptyState, Spinner } from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type { AppMetadataPage, AppMetadataReport } from "../admin-client";
import { useDeviceAppMetadata } from "../admin-hooks";
import { errorMessage } from "./guards";
import { when } from "./common";
import type { MetadataReadState } from "./device-metadata";
import {
  describeBytes,
  disclosureLabel,
  missingFromFullPage,
  payloadIsEmpty,
  payloadText,
  readState,
  reportCountLabel,
} from "./device-metadata";

/**
 * The App metadata section on an installation's record.
 *
 * Its own module rather than three hundred more lines of `user-detail.tsx`,
 * for the reason `analytics-common.tsx` is its own module: the device card is
 * one screen's composition of facts that arrived with the account, and this is
 * a second read, behind a second permission, with its own failure — so it is
 * the one part of that card that can be reasoned about, and rendered, alone.
 *
 * It is split in two on purpose. `DeviceAppMetadata` owns the disclosure, the
 * query and the four pieces of state a click can move; `MetadataPanel` and
 * `MetadataReport` own everything that is DRAWN and take all of it as props.
 * That is what makes the states provable: there is no browser on the build box,
 * so a component whose branches are reachable only by clicking is a component
 * whose branches are only ever typechecked.
 */

/**
 * The LOOKING half of the device permissions, and a genuinely different grant
 * from `devices:revoke` — Billing Ops holds `users:read` and neither of these,
 * and the Admin API's own test asserts the two device permissions apart.
 *
 * It gates this section, and it gates it by ABSENCE rather than by an
 * explanatory panel: every other fact on the device card arrived with the
 * account, and a per-device card headed "not your permission" would repeat
 * itself once per installation down a page that may list two hundred of them.
 */
export const DEVICES_READ = "devices:read";

/** The report an operator asked to see whole, and the namespace it came from.
 *  The namespace is remembered with it because it is the way back to a report
 *  the shortened full page did not return — see MetadataPanel. */
interface WantedReport {
  id: string;
  source: string;
}

/**
 * What ONE installation has reported about itself — the arbitrary
 * self-description a shipped client posts about the machine it runs on.
 *
 * Its own query, behind its OWN permission (`devices:read`), and rendered on
 * the device card rather than on a screen of its own because this is the page
 * an operator is already on when somebody asks what is installed on the laptop.
 * It is the only panel on that card whose data does not arrive with the
 * account, so it is also the only one that can fail on its own — and it does,
 * into an alert inside this disclosure, without taking the key history, the
 * approval history or the Block button with it.
 *
 * CLOSED, and the read is not issued until it is opened. That is not a
 * performance decision: every one of these reads writes an `admin_actions` row
 * naming the operator, the installation and how many payloads travelled,
 * because a metadata document is arbitrary detail about a customer's own
 * machine — hostnames, plugin inventories, licence tiers, disk serials. An
 * account may list two hundred devices, and a section that fired one read per
 * device on load would write two hundred audit rows for an operator who came
 * here to look at one. Opening the disclosure is the act; the trail records
 * acts.
 *
 * Everything the section draws is UNTRUSTED CLIENT INPUT. Nothing here reads a
 * key out of a payload, nothing chooses a renderer from one, and the document
 * itself goes into a `<pre>` as a text child — see MetadataReport.
 */
export function DeviceAppMetadata({ deviceID }: { deviceID: string }) {
  // The disclosure's own state drives `enabled`, so a closed section issues no
  // request at all. It is read back off the DOM's toggle rather than assumed,
  // which keeps the native control the source of truth.
  const [open, setOpen] = useState(false);
  // Whether whole payloads were ASKED for. What is on screen is `page.full`,
  // which is a different thing while a refetch is in flight.
  const [full, setFull] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  // The report an operator asked to see whole, remembered because `?full=true`
  // shortens the page and may not bring it back. See missingFromFullPage.
  const [wanted, setWanted] = useState<WantedReport | null>(null);
  // Which rows are expanded. A Set rather than a single id: two payloads open
  // at once is a comparison, and an accordion that closed the other one would
  // be answering a question the operator did not ask.
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(() => new Set<string>());

  const reports = useDeviceAppMetadata(deviceID, { full, source: source ?? undefined }, open);
  const page = reports.data;
  // A failure with NOTHING to show. An error with a page behind it is a
  // refresh that failed over an answer that is still on screen, and readState
  // keeps the two apart.
  const state = readState(open, Boolean(reports.error) && !page, Boolean(page));

  const toggleRow = (id: string, expanded: boolean) =>
    setOpenRows((current) => {
      const next = new Set(current);
      if (expanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

  return (
    <details
      className="esm-device__history"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>
        App metadata{" "}
        <span className="esm-muted-text">
          ({disclosureLabel(state, page?.reports.length ?? 0, page?.truncated ?? false)})
        </span>
      </summary>
      {/* Rendered only while open, so the query's "never asked" state has no
          spinner sitting behind a closed disclosure waiting to resolve. */}
      {open ? (
        <MetadataPanel
          state={state}
          page={page}
          error={reports.error}
          busy={reports.isFetching}
          source={source}
          wanted={wanted}
          openRows={openRows}
          onToggleRow={toggleRow}
          onClearSource={() => setSource(null)}
          onNarrowToSource={setSource}
          onBackToSummaries={() => {
            setFull(false);
            setWanted(null);
          }}
          onAskForWhole={(report) => {
            setWanted({ id: report.id, source: report.source });
            // Open the row it came from, so the document the operator asked
            // for is visible when it arrives instead of behind one more click.
            setOpenRows((current) => new Set(current).add(report.id));
            setFull(true);
          }}
        />
      ) : null}
    </details>
  );
}

/**
 * Everything the section draws, and nothing it remembers.
 *
 * Every branch here is reachable from props alone, which is the point: the
 * empty history, the two failures, the shortened full page and the report that
 * fell off it are the states that matter and none of them can be reached by
 * rendering the happy path.
 */
export function MetadataPanel({
  state,
  page,
  error,
  busy,
  source,
  wanted,
  openRows,
  onToggleRow,
  onClearSource,
  onNarrowToSource,
  onBackToSummaries,
  onAskForWhole,
}: {
  state: MetadataReadState;
  page: AppMetadataPage | undefined;
  error: unknown;
  /** A read is in flight. It disables the fetch-whole controls rather than
   *  replacing the page, which is what `placeholderData` is for. */
  busy: boolean;
  source: string | null;
  wanted: WantedReport | null;
  openRows: ReadonlySet<string>;
  onToggleRow: (id: string, expanded: boolean) => void;
  onClearSource: () => void;
  onNarrowToSource: (source: string) => void;
  onBackToSummaries: () => void;
  onAskForWhole: (report: AppMetadataReport) => void;
}) {
  const rows = page?.reports ?? [];
  // A 403 can still arrive: the permission list this console holds is the one
  // from sign-in, and a role changed underneath an open page is exactly the
  // case where the server's answer is the true one.
  const forbidden = isApiError(error) && error.status === 403;

  let body: ReactNode;
  if (state === "loading") {
    body = <Spinner label="Reading what this installation has reported" />;
  } else if (state === "failed") {
    body = (
      <Alert
        tone={forbidden ? "info" : "danger"}
        title={forbidden ? "Not your permission" : "Could not read this installation's metadata"}
      >
        {forbidden
          ? "Reading what an installation reports about itself needs devices:read, which your roles do not grant."
          : errorMessage(error)}
      </Alert>
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        title={
          source
            ? `This installation has never reported anything under ${source}`
            : "This installation has never reported anything about itself"
        }
      >
        {/* "Never reported" and "reported nothing" are different facts about
            the client, and only one of them is a client that is working. A
            report of {} — the inventory of a machine that has none of the
            thing being inventoried — is a row in this list with an empty
            document in it, not an absence from it. */}
        Nothing has ever been posted from it. That is not the same as a client that posted an
        empty document: an empty report would be listed here, with <code>{"{}"}</code> as its
        payload.
      </EmptyState>
    );
  } else {
    body = rows.map((report) => (
      <MetadataReport
        key={report.id}
        report={report}
        inlineLimit={page?.payload_inline_limit_bytes ?? 0}
        expanded={openRows.has(report.id)}
        wanted={wanted?.id === report.id}
        busy={busy}
        onToggle={onToggleRow}
        onAskForWhole={onAskForWhole}
      />
    ));
  }

  return (
    <div className="esm-metadata">
      <p className="esm-muted-text">
        What this installation says about itself, newest confirmation first. It is the client's
        own words, stored verbatim and never interpreted — treat every value below as untrusted.
        Reading it is recorded in the admin audit trail, with how many whole documents were
        handed over.
      </p>
      {source ? (
        <p className="esm-metadata__notice">
          Narrowed to the <code>{source}</code> namespace.{" "}
          <Button variant="secondary" onClick={onClearSource}>
            Show every source
          </Button>
        </p>
      ) : null}
      {/* Driven by the RESPONSE's flag, not by the request state in the shell:
          during a refetch the elided page is still what is on screen, and a
          notice describing the page that has not arrived yet describes
          nothing. */}
      {page?.full ? (
        <Alert tone="info" title={`Whole documents — at most ${page.limit} reports`}>
          Serving payloads whole removes the only thing bounding a row's size, so the server
          shortens the page to match. Reports older than the {page.limit} below are not on it.{" "}
          <Button variant="secondary" onClick={onBackToSummaries}>
            Back to the summary list
          </Button>
        </Alert>
      ) : null}
      {wanted && missingFromFullPage(wanted.id, rows, page?.full ?? false) ? (
        <Alert tone="warning" title="The report you asked for is not on this page">
          Whole documents come {page?.limit ?? 10} at a time, and that report is older than the
          ones listed below.{" "}
          {source === wanted.source ? (
            <>
              It is not among the newest whole documents for <code>{wanted.source}</code> either —
              more recent reports under that source have crowded it out.
            </>
          ) : (
            <>
              Narrowing to its own namespace is what reaches it.{" "}
              <Button variant="secondary" onClick={() => onNarrowToSource(wanted.source)}>
                Only {wanted.source} reports
              </Button>
            </>
          )}
        </Alert>
      ) : null}
      {/* A refresh that failed over a page that is still on screen. The rows
          below are real and stay; what is not true any more is that they are
          current. */}
      {state === "ready" && error ? (
        <Alert tone="danger" title="The list below could not be refreshed">
          {errorMessage(error)} What is shown is the last answer that arrived.
        </Alert>
      ) : null}
      {body}
    </div>
  );
}

/**
 * One report: the summary says what it IS, the body carries what it SAYS.
 *
 * Source, schema version, how often it has been confirmed and both timestamps
 * sit in the `<summary>`, so a whole history reads without a single click —
 * which is the point, because the thing behind the click is a document of
 * arbitrary size and arbitrary content, and opening one is a decision.
 *
 * The payload is rendered as TEXT and nothing else. No HTML, no linkification,
 * no renderer chosen by a key, no `dangerouslySetInnerHTML` anywhere near it:
 * a payload is written by a customer's client, and the console — where an
 * operator with every permission in this realm is signed in — is the single
 * most rewarding place in the system to get a script into. `payloadText`
 * returns a string, React escapes it, and the `<pre>` is where it lands.
 */
export function MetadataReport({
  report,
  inlineLimit,
  expanded,
  wanted,
  busy,
  onToggle,
  onAskForWhole,
}: {
  report: AppMetadataReport;
  inlineLimit: number;
  expanded: boolean;
  wanted: boolean;
  busy: boolean;
  onToggle: (id: string, expanded: boolean) => void;
  onAskForWhole: (report: AppMetadataReport) => void;
}) {
  // The PRESENCE of the document decides what there is to draw, and
  // `payload_truncated` is the server's statement that the two agree. Branching
  // on the value is what lets the "there is a document here" arm be typed as
  // one — a flag cannot narrow an optional field, and a flag trusted over the
  // field would render `undefined` into a <pre>.
  const payload = report.payload;

  return (
    <details
      className={
        wanted ? "esm-metadata__report esm-metadata__report--wanted" : "esm-metadata__report"
      }
      open={expanded}
      onToggle={(event) => onToggle(report.id, event.currentTarget.open)}
    >
      <summary>
        <code className="esm-metadata__source">{report.source}</code>{" "}
        <span className="esm-muted-text">
          {/* A client that stamps no version is a real answer and not a
              missing one, so it is said rather than left blank. */}
          {report.schema_version ? `schema ${report.schema_version}` : "no schema version"} ·{" "}
          {reportCountLabel(report.report_count)} · first seen {when(report.first_seen_at)} · last
          confirmed {when(report.last_seen_at)}
        </span>
      </summary>
      {payload === undefined ? (
        <div className="esm-metadata__body">
          {/* The size is the number the elision rule was applied to, and it is
              stated beside the limit that elided it — so the absence is
              explained rather than merely shown. */}
          <p className="esm-muted-text">
            This document is {describeBytes(report.payload_bytes)}, over the{" "}
            {describeBytes(inlineLimit)} this list sends inline, so it was not returned with it.
          </p>
          <Button variant="secondary" busy={busy} onClick={() => onAskForWhole(report)}>
            Fetch the whole document
          </Button>
          <p className="esm-muted-text">
            Re-reads this installation with every payload whole. The page gets shorter when it
            does — that flag removes the only bound on a row's size — and the audit trail records
            that whole documents were taken.
          </p>
        </div>
      ) : (
        <div className="esm-metadata__body">
          <p className="esm-muted-text">{describeBytes(report.payload_bytes)} as sent.</p>
          {payloadIsEmpty(payload) ? (
            <p className="esm-blank-value">
              The client posted an empty document. It reported, and what it reported was nothing —
              which is a different fact from an installation that has never reported at all.
            </p>
          ) : null}
          {/* A text child, and the only thing this component does with the
              document. Whatever is in it comes out as characters. */}
          <pre className="esm-metadata__payload">
            <code>{payloadText(payload)}</code>
          </pre>
        </div>
      )}
    </details>
  );
}
